import { z } from 'zod';
import { isDateKey, todayDateKey } from '../../../utils/dateKey';

/** Backend cap on the reference string (`StoreQuoteRequest.php` `dealer_payment.reference`). */
const MAX_TEXT_LENGTH = 120;

export interface DealerCollectedPaymentFormValues {
    collectedBy: 'elemis' | 'dealer';
    /** The Cash Sale Invoice Number — stored as the payment `reference` on the wire. */
    reference: string;
    /** `YYYY-MM-DD`. */
    collectedAt: string;
    /** The dealer's attestation that they collected the payment. */
    confirmed: boolean;
    sendViaEmail: boolean;
    sendViaSms: boolean;
}

/**
 * Only the `dealer` branch is constrained — `elemis` never carries a receipt.
 * The amount is single-sourced upstream, not a field here.
 */
export const buildDealerCollectedPaymentSchema = () =>
    z
        .object({
            collectedBy: z.enum(['elemis', 'dealer']),
            reference: z.string(),
            collectedAt: z.string(),
            confirmed: z.boolean(),
            sendViaEmail: z.boolean(),
            sendViaSms: z.boolean(),
        })
        .superRefine((values, ctx) => {
            if (values.collectedBy !== 'dealer') {
                return;
            }

            if (values.reference.trim().length === 0) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['reference'],
                    message: 'Cash Sale Invoice Number is required',
                });
            } else if (values.reference.length > MAX_TEXT_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['reference'],
                    message: `Cash Sale Invoice Number must be ${MAX_TEXT_LENGTH} characters or fewer`,
                });
            }

            if (!isDateKey(values.collectedAt)) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['collectedAt'],
                    message: 'Enter a valid collection date',
                });
                // Read at parse time, not at build time, so a session open across
                // midnight compares against the current day.
            } else if (values.collectedAt > todayDateKey()) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['collectedAt'],
                    message: 'The collection date cannot be in the future',
                });
            }

            if (!values.confirmed) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['confirmed'],
                    message: 'You must confirm the collection to continue',
                });
            }
        });

export type DealerCollectedPaymentSchema = ReturnType<typeof buildDealerCollectedPaymentSchema>;
